import { zodResolver } from "@hookform/resolvers/zod"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { Input } from "design-system/atoms/Input"
import { Separator } from "design-system/atoms/Separator"
import { Section } from "design-system/molecules/Section"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { CopyButtonWrapper } from "design-system/organisms/chat/CopyButton"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { Bot, Building2, Mail } from "lucide-react"
import { useCompanyQuery } from "modules/settings/hooks/useCompanyQuery"
import { useUpdateCompanyMutation } from "modules/settings/hooks/useUpdateCompanyMutation"
import { Loading } from "modules/shared/components/Loading"
import React, { Suspense, useEffect } from "react"
import { type Resolver, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { buildDefaultEmailSignatureHtml, CompanySchema, Locale } from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { AgentCustomizationForm } from "./AgentCustomizationForm"
import { LogoPicker } from "./LogoPicker"

const CUSTOMIZATION_TABS = [
  { value: "company", icon: Building2, labelKey: "settings.company.customization.tabs.company" },
  { value: "agent", icon: Bot, labelKey: "settings.company.customization.tabs.agent" },
  { value: "email", icon: Mail, labelKey: "settings.company.customization.tabs.email" },
] as const

const EmailSignatureEditor = React.lazy(() => import("./EmailSignatureEditor"))

const routeApi = getRouteApi("/_base/_auth/settings/customization")

export function CustomizationForm() {
  const { t } = useTranslation()
  const { data: company, isPending } = useCompanyQuery()
  const { tab } = routeApi.useSearch()
  const navigate = useNavigate()

  const form = useForm<z.infer<typeof CompanySchema>>({
    // Resolver validates a subset of CompanySchema fields + logo, but the form manages the full type
    resolver: zodResolver(
      CompanySchema.pick({
        name: true,
        locale: true,
        website: true,
      }).extend({
        logo: z.union([z.instanceof(File), z.string()]),
      }),
    ) as unknown as Resolver<z.infer<typeof CompanySchema>>,
    defaultValues: company,
  })

  useEffect(() => {
    if (company) {
      form.reset(company)
      form.trigger()
    }
  }, [company, form])

  const { mutateAsync: updateCompany, isPending: isSavingSignature } = useUpdateCompanyMutation()

  if (isPending) {
    return <Loading />
  }

  async function onUpdate(
    e?:
      | React.FocusEvent<HTMLInputElement>
      | React.FocusEvent<HTMLTextAreaElement>
      | React.FormEvent<HTMLFormElement>,
    logo?: FileList,
  ) {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    const valuesHaveChanged = JSON.stringify(form.getValues()) !== JSON.stringify(company)

    if (!valuesHaveChanged && !logo) {
      return
    }

    function onUndo() {
      const currentCompany = { ...company }
      form.reset(currentCompany)

      void updateCompany({
        ...currentCompany,
        logo: undefined,
      })
    }

    const currentValues = form.getValues()

    await updateCompany({
      ...currentValues,
      logo: logo || undefined,
    })

    toast.success(t("settings.company.customization.form.updated"), {
      action: {
        label: t("shared.undo"),
        onClick: onUndo,
      },
    })
  }

  async function onSaveSignature(emailSignature: string) {
    await updateCompany({ emailSignature })
    toast.success(t("settings.company.customization.form.updated"))
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col items-top gap-2 w-full justify-between">
        <Section
          title={t("settings.company.customization.title")}
          description={t("settings.company.customization.description", {
            name: company?.name,
          })}
        />
        <Separator />
      </div>
      <Tabs
        variant="pill"
        orientation="vertical"
        value={tab}
        onValueChange={(value) => {
          if (value === "company" || value === "agent" || value === "email") {
            navigate({ to: "/settings/customization", search: { tab: value } })
          }
        }}
        className="flex lg:flex-row flex-col items-start gap-6"
      >
        <TabsList className="h-auto lg:flex-col gap-1 p-0 bg-background shrink-0 flex flex-row items-start lg:items-stretch justify-start">
          {CUSTOMIZATION_TABS.map(({ value, icon: Icon, labelKey }) => (
            <TabsTrigger
              key={value}
              value={value}
              className="lg:w-full lg:min-w-48 justify-start data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-3 py-1.5"
            >
              <Icon className="size-4" />
              <span>{t(labelKey)}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="w-full">
          <TabsContent value="company">
            <Form
              {...form}
              onSubmit={onUpdate}
              errorTranslations={getErrorTranslations()}
              className="gap-6"
            >
              <div className="flex flex-col md:flex-row gap-8">
                <LogoPicker
                  onChangeLogo={(logo) => {
                    onUpdate(undefined, logo)
                  }}
                />
                <div className="flex flex-col gap-4 max-w-xs w-full">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("settings.company.customization.form.name")}</FormLabel>
                        <FormControl>
                          <CopyButtonWrapper>
                            <Input
                              {...field}
                              value={field.value ?? ""}
                              className="disabled:cursor-auto !text-base"
                              onBlur={onUpdate}
                            />
                          </CopyButtonWrapper>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("settings.company.customization.form.website")}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ""}
                            className="disabled:cursor-auto !text-base"
                            onBlur={onUpdate}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex flex-col gap-4 max-w-xs w-full">
                  <FormField
                    control={form.control}
                    name="locale"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("settings.company.customization.form.locale")}</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value)
                              void onUpdate()
                            }}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t("shared.locale")} />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.values(Locale).map((locale) => (
                                <SelectItem key={locale} value={locale}>
                                  {t(`shared.localeOptions.${locale}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormDescription>
                          {t("settings.company.customization.form.localeDescription")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </Form>
          </TabsContent>
          <TabsContent value="agent">
            <AgentCustomizationForm />
          </TabsContent>
          <TabsContent value="email">
            <div className="flex flex-col gap-3">
              <Suspense fallback={<Loading />}>
                <EmailSignatureEditor
                  initialHtml={
                    company?.emailSignature ||
                    buildDefaultEmailSignatureHtml({ logo: company?.logo })
                  }
                  defaultHtml={buildDefaultEmailSignatureHtml({ logo: company?.logo })}
                  isSaving={isSavingSignature}
                  onSave={onSaveSignature}
                />
              </Suspense>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}
